import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Phone, UserCheck } from "lucide-react";

/**
 * Hard-coded business rule (Asia/Kolkata):
 *   Before 12:00 IST → default dashboard to Today's bookings.
 *   12:00 IST onwards → default to Tomorrow's bookings (team is prepping for next day).
 * Returns { dateStr: 'YYYY-MM-DD', isTomorrow: boolean }.
 */
function defaultDashboardDate() {
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", hour12: false,
  });
  const parts = Object.fromEntries(fmt.formatToParts(new Date()).map((p) => [p.type, p.value]));
  const istHour = Number(parts.hour);
  const todayIst = `${parts.year}-${parts.month}-${parts.day}`;
  if (istHour < 12) return { dateStr: todayIst, isTomorrow: false };
  const [y, m, d] = todayIst.split("-").map(Number);
  const tomorrow = new Date(Date.UTC(y, m - 1, d) + 24 * 60 * 60 * 1000);
  const dateStr = `${tomorrow.getUTCFullYear()}-${String(tomorrow.getUTCMonth() + 1).padStart(2, "0")}-${String(tomorrow.getUTCDate()).padStart(2, "0")}`;
  return { dateStr, isTomorrow: true };
}

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [reports, setReports] = useState(null);
  const [{ dateStr, isTomorrow }] = useState(defaultDashboardDate);
  const dayLabel = isTomorrow ? "Tomorrow" : "Today";

  useEffect(() => {
    api.get(`/bookings/today-summary?date=${dateStr}`).then(({ data }) => setSummary(data));
    api.get("/bookings?upcoming=true").then(({ data }) => setUpcoming(data));
    api.get("/reports/overview").then(({ data }) => setReports(data));
  }, [dateStr]);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">Dashboard</h1>
          <p className="text-sm opacity-65 mt-1">{summary?.date}</p>
        </div>
        <Link
          to="/admin/quick-attendance"
          data-testid="open-quick-attendance"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full btn-brand text-sm font-medium"
        >
          <UserCheck size={14} /> {dayLabel}'s Quick Attendance
        </Link>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={`${dayLabel}'s Bookings`} value={summary?.total ?? "—"} testid="stat-today-total" />
        <Stat label="Total Bookings" value={reports?.total_bookings ?? "—"} testid="stat-total-bookings" />
        <Stat label="Paid Bookings" value={reports?.paid_bookings ?? "—"} testid="stat-paid-bookings" />
        <Stat label="Deposits Collected" value={reports ? `₹${reports.total_deposits.toLocaleString("en-IN")}` : "—"} testid="stat-deposits" />
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <section className="bg-white rounded-md border border-[color:var(--border)] p-5">
          <h2 className="font-heading text-2xl">{dayLabel} by Pooja</h2>
          {summary && Object.keys(summary.by_pooja || {}).length === 0 && (
            <p className="text-sm opacity-65 mt-3">No bookings for {dayLabel.toLowerCase()}.</p>
          )}
          <div className="mt-4 space-y-3">
            {summary && Object.entries(summary.by_pooja || {}).map(([slug, info]) => (
              <details key={slug} className="border border-[color:var(--border)] rounded-md p-3" data-testid={`today-${slug}`}>
                <summary className="cursor-pointer flex items-center justify-between">
                  <span className="font-medium">{info.pooja_name}</span>
                  <span className="text-sm text-[color:var(--brand)]">{info.count}</span>
                </summary>
                <div className="mt-3 divide-y divide-[color:var(--border)]">
                  {info.bookings.map((b) => (
                    <div key={b.id} className="py-2 flex items-center justify-between text-sm">
                      <div>
                        <div className="font-medium">{b.devotee_name}</div>
                        <div className="opacity-65 text-xs">{b.payment_status} · {b.attendance_status}</div>
                      </div>
                      <a href={`tel:${b.mobile}`} className="inline-flex items-center gap-1 text-xs text-[color:var(--brand)]" data-testid={`call-${b.id}`}>
                        <Phone size={12} /> {b.mobile}
                      </a>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-md border border-[color:var(--border)] p-5">
          <h2 className="font-heading text-2xl">Upcoming Bookings</h2>
          <div className="mt-4 divide-y divide-[color:var(--border)]">
            {upcoming.slice(0, 12).map((b) => (
              <div key={b.id} className="py-3 flex items-center justify-between" data-testid={`upcoming-${b.id}`}>
                <div>
                  <div className="font-medium text-sm">{b.devotee_name} <span className="opacity-60 font-normal">· {b.pooja_name}</span></div>
                  <div className="text-xs opacity-65">{b.booking_date} · {b.mobile}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-[color:var(--bg-2)]">{b.attendance_status}</span>
              </div>
            ))}
            {upcoming.length === 0 && <div className="text-sm opacity-65 py-2">No upcoming bookings.</div>}
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-white rounded-md border border-[color:var(--border)] p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] opacity-60">{label}</div>
      <div className="font-heading text-3xl mt-2 text-[color:var(--brand)]">{value}</div>
    </div>
  );
}
